import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { SITE_URL, SITE_NAME } from "@/config/site";
import { normalizeBusinessSchema } from "@/lib/businessSchema";

const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.jpg`;

interface SeoProps {
  title: string;
  description: string;
  /** Optional path override, e.g. "/services". Query strings are stripped. */
  canonicalPath?: string;
  ogImage?: string;
  noIndex?: boolean;
  schema?: Record<string, unknown> | Record<string, unknown>[];
}

/**
 * Normalises a path into a canonical suffix: no query string, no hash,
 * no trailing slash, lowercased.
 */
function normalisePath(path: string): string {
  let p = path.split("?")[0].split("#")[0];
  // Strip an accidental absolute URL down to its pathname.
  if (/^https?:\/\//i.test(p)) {
    try {
      p = new URL(p).pathname;
    } catch {
      p = "/";
    }
  }
  p = p.toLowerCase();
  if (!p.startsWith("/")) p = `/${p}`;
  while (p.length > 1 && p.endsWith("/")) p = p.slice(0, -1);
  return p === "/" ? "" : p;
}

/**
 * Single source of truth for per-page <head> metadata.
 * The canonical host is always the hardcoded SITE_URL constant, never
 * window.location.hostname, so www / preview hosts cannot self-canonicalise.
 */
export function Seo({
  title,
  description,
  canonicalPath,
  ogImage = DEFAULT_OG_IMAGE,
  noIndex = false,
  schema,
}: SeoProps) {
  const location = useLocation();
  const path = normalisePath(canonicalPath ?? location.pathname);
  const canonicalUrl = `${SITE_URL}${path}`;

  const absoluteOgImage = /^https?:\/\//i.test(ogImage)
    ? ogImage
    : `${SITE_URL}${ogImage.startsWith("/") ? "" : "/"}${ogImage}`;

  const schemas = schema ? (Array.isArray(schema) ? schema : [schema]) : [];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      <meta
        name="robots"
        content={noIndex ? "noindex, nofollow" : "index, follow, max-image-preview:large"}
      />

      {/* Open Graph */}
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={absoluteOgImage} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteOgImage} />

      {/* JSON-LD */}
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(normalizeBusinessSchema(s))}
        </script>
      ))}
    </Helmet>
  );
}

export default Seo;
